import React, { useState, useMemo } from "react";
import { Printer } from "lucide-react";
import { obtenerReportePagos } from "../services/reportesService";
import PageHeader from "../components/PageHeader";
import Toast, { useToast } from "../components/Toast";

const today = new Date();
const toHtmlDate = (d) => d.toISOString().slice(0, 10);
const DEFAULT_FECHA_INI = toHtmlDate(new Date(today.getFullYear(), today.getMonth(), 1));
const DEFAULT_FECHA_FIN = toHtmlDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));

const htmlToApiDate = (d) => (d ? d.replace(/-/g, "/") : "");

const displayDate = (d) => {
  if (!d) return "";
  // handles both yyyy/MM/dd and yyyy-MM-dd
  const parts = d.includes("/") ? d.split("/") : d.split("-");
  if (parts.length !== 3) return d;
  const [y, m, day] = parts;
  return `${day}/${m}/${y}`;
};

const longDate = (htmlDate) => {
  if (!htmlDate) return "";
  const d = new Date(htmlDate + "T00:00:00");
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
};

const fmt = (n) =>
  `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Vista de impresión ───────────────────────────────────────────────────────
function PrintView({ grupos, fechaFin, moneda, totalesGlobal }) {
  const monedaLabel = moneda === "D" ? "Dólares" : "Pesos";

  return (
    <div style={{ padding: "16px 20px", fontFamily: "Arial, sans-serif", fontSize: 10, color: "#000" }}>
      {/* Encabezado */}
      <table style={{ width: "100%", marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ width: "15%", verticalAlign: "middle" }}>
              <img src="/LMD.jpg" alt="LMD" style={{ height: 52, objectFit: "contain" }} />
            </td>
            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
              <div style={{ fontSize: 15, fontWeight: "bold" }}>LMD INTEGRADORA DE</div>
              <div style={{ fontSize: 13, fontWeight: "bold", marginTop: 4 }}>Reporte de pagos de clientes</div>
              <div style={{ fontSize: 10, marginTop: 4 }}>
                Reporte al: {longDate(fechaFin)}&nbsp; En {monedaLabel}
              </div>
            </td>
            <td style={{ width: "10%", textAlign: "right", verticalAlign: "top", fontSize: 9 }}>1 De 1</td>
          </tr>
        </tbody>
      </table>

      {/* Cabecera de columnas */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #333" }}>
            <th style={{ ...th, width: "6%",  textAlign: "left"  }}>CTE.</th>
            <th style={{ ...th, width: "22%", textAlign: "left"  }}>Nombre</th>
            <th style={{ ...th, width: "9%"  }}>Fecha</th>
            <th style={{ ...th, width: "4%"  }}>Doc.<br/>Pago</th>
            <th style={{ ...th, width: "7%"  }}>C. Pago</th>
            <th style={{ ...th, width: "12%" }}>Doc. Pago</th>
            <th style={{ ...th, width: "9%"  }}>Doc. a Pagar</th>
            <th style={{ ...th, width: "10%", textAlign: "right" }}>Subtotal</th>
            <th style={{ ...th, width: "10%", textAlign: "right" }}>I.V.A</th>
            <th style={{ ...th, width: "11%", textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {grupos.map((g) => {
            const totCliente = g.rows.reduce(
              (acc, r) => ({
                subtotal: acc.subtotal + Number(r.subtotal),
                iva:      acc.iva      + Number(r.iva),
                total:    acc.total    + Number(r.total),
              }),
              { subtotal: 0, iva: 0, total: 0 }
            );
            return (
              <React.Fragment key={g.clave}>
                {/* Fila de cliente */}
                <tr>
                  <td style={{ ...td, fontWeight: "bold", paddingTop: 8 }}>{g.clave}</td>
                  <td colSpan={9} style={{ ...td, fontWeight: "bold", paddingTop: 8 }}>{g.nombre}</td>
                </tr>
                {/* Filas de pago */}
                {g.rows.map((r, i) => (
                  <tr key={i}>
                    <td style={td} />
                    <td style={td} />
                    <td style={{ ...td, textAlign: "center" }}>{displayDate(r.fecha)}</td>
                    <td style={{ ...td, textAlign: "center" }}>{(r.seriePago || "").toUpperCase()}</td>
                    <td style={{ ...td, textAlign: "center" }}>{r.folioPago}</td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {r.tipoDocPago} {r.numDocPago}
                    </td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {r.tipoDocAPagar} {r.numDocAPagar}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>{fmt(r.subtotal)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{fmt(r.iva)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{fmt(r.total)}</td>
                  </tr>
                ))}
                {/* Total por cliente */}
                <tr style={{ borderTop: "1px solid #ccc" }}>
                  <td colSpan={7} style={{ ...td, textAlign: "right", fontWeight: "bold" }}>TOTAL</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: "bold" }}>{fmt(totCliente.subtotal)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: "bold" }}>{fmt(totCliente.iva)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: "bold" }}>{fmt(totCliente.total)}</td>
                </tr>
              </React.Fragment>
            );
          })}

          {/* Total global */}
          <tr style={{ borderTop: "2px solid #333", marginTop: 8 }}>
            <td colSpan={7} style={{ ...td, textAlign: "right", fontWeight: "bold", paddingTop: 6 }}>TOTAL</td>
            <td style={{ ...td, textAlign: "right", fontWeight: "bold", paddingTop: 6 }}>{fmt(totalesGlobal.subtotal)}</td>
            <td style={{ ...td, textAlign: "right", fontWeight: "bold", paddingTop: 6 }}>{fmt(totalesGlobal.iva)}</td>
            <td style={{ ...td, textAlign: "right", fontWeight: "bold", paddingTop: 6 }}>{fmt(totalesGlobal.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const th = { padding: "4px 6px", fontWeight: "bold", textAlign: "center" };
const td = { padding: "2px 6px" };

// ── Página principal ─────────────────────────────────────────────────────────
export default function ReportePagosPage() {
  const [filters, setFilters] = useState({
    fechaInicial: DEFAULT_FECHA_INI,
    fechaFinal:   DEFAULT_FECHA_FIN,
    moneda:       "P",
  });
  const [filas,   setFilas]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [consultado, setConsultado] = useState({ fechaFin: "", moneda: "P" });

  const empresaId = Number(localStorage.getItem("empresaId") || 1);
  const { toasts, closeToast, error: toastError } = useToast();

  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const handleConsultar = async () => {
    setLoading(true);
    setFilas(null);
    try {
      const data = await obtenerReportePagos({
        fechaInicial: htmlToApiDate(filters.fechaInicial),
        fechaFinal:   htmlToApiDate(filters.fechaFinal),
        moneda:       filters.moneda,
        empresaId,
      });
      setFilas(data || []);
      setConsultado({ fechaFin: filters.fechaFinal, moneda: filters.moneda });
    } catch {
      toastError("Error al consultar el reporte de pagos.");
    } finally {
      setLoading(false);
    }
  };

  // Agrupar por cliente
  const grupos = useMemo(() => {
    if (!filas) return [];
    const map = new Map();
    for (const r of filas) {
      if (!map.has(r.claveCliente)) {
        map.set(r.claveCliente, { clave: r.claveCliente, nombre: r.nombreCliente, rows: [] });
      }
      map.get(r.claveCliente).rows.push(r);
    }
    return Array.from(map.values());
  }, [filas]);

  const totalesGlobal = useMemo(() => {
    if (!filas) return { subtotal: 0, iva: 0, total: 0 };
    return filas.reduce(
      (acc, r) => ({
        subtotal: acc.subtotal + Number(r.subtotal),
        iva:      acc.iva      + Number(r.iva),
        total:    acc.total    + Number(r.total),
      }),
      { subtotal: 0, iva: 0, total: 0 }
    );
  }, [filas]);

  const totalPagos = filas ? filas.length : 0;

  return (
    <>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Reporte de Pagos" subtitle="Pagos de clientes por período" />

      {/* Filtros */}
      <div className="lmd-card reporte-no-print" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="lmd-card-header">
          <h3 className="lmd-card-title">Filtros</h3>
        </div>
        <div className="lmd-card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "var(--sp-4)", alignItems: "end" }}>
            <div className="lmd-field">
              <label className="lmd-label">Fecha Inicial</label>
              <input className="lmd-input" type="date" name="fechaInicial" value={filters.fechaInicial} onChange={onChange} />
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Fecha Final</label>
              <input className="lmd-input" type="date" name="fechaFinal" value={filters.fechaFinal} onChange={onChange} />
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Moneda</label>
              <select className="lmd-select" name="moneda" value={filters.moneda} onChange={onChange} style={{ width: "100%" }}>
                <option value="P">Pesos (MXN)</option>
                <option value="D">Dólares (USD)</option>
              </select>
            </div>
            <div className="lmd-field">
              <label className="lmd-label">&nbsp;</label>
              <button
                className="lmd-btn lmd-btn-primary"
                onClick={handleConsultar}
                disabled={loading}
                style={{ width: "100%" }}
              >
                {loading ? "Consultando…" : "Consultar"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Resultados */}
      {filas !== null && (
        <div className="lmd-card reporte-no-print">
          <div className="lmd-card-header">
            <h3 className="lmd-card-title">
              Resultados&nbsp;
              <span style={{ fontWeight: "normal", color: "var(--clr-text-muted)", fontSize: "var(--fs-sm)" }}>
                {totalPagos} pago{totalPagos !== 1 ? "s" : ""}
              </span>
            </h3>
            <button
              className="lmd-btn lmd-btn-secondary"
              onClick={() => window.print()}
              disabled={totalPagos === 0}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Printer size={15} /> Imprimir
            </button>
          </div>

          {totalPagos === 0 ? (
            <div style={{ padding: "32px", textAlign: "center", color: "var(--clr-text-muted)" }}>
              No se encontraron pagos para el período seleccionado.
            </div>
          ) : (
            <>
              {/* Tabla agrupada por cliente */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--fs-sm)" }}>
                  <thead>
                    <tr style={{ background: "var(--clr-table-head-bg, #f3f4f6)", borderBottom: "2px solid var(--clr-border)" }}>
                      <th style={scrTh}>Fecha</th>
                      <th style={scrTh}>C. Pago</th>
                      <th style={scrTh}>Doc. Pago</th>
                      <th style={scrTh}>Doc. a Pagar</th>
                      <th style={{ ...scrTh, textAlign: "right" }}>Subtotal</th>
                      <th style={{ ...scrTh, textAlign: "right" }}>I.V.A</th>
                      <th style={{ ...scrTh, textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupos.map((g) => {
                      const totCte = g.rows.reduce(
                        (acc, r) => ({
                          subtotal: acc.subtotal + Number(r.subtotal),
                          iva:      acc.iva      + Number(r.iva),
                          total:    acc.total    + Number(r.total),
                        }),
                        { subtotal: 0, iva: 0, total: 0 }
                      );
                      return (
                        <React.Fragment key={g.clave}>
                          {/* Fila cliente */}
                          <tr style={{ background: "var(--clr-primary-subtle, #EFF6FF)" }}>
                            <td colSpan={7} style={{
                              padding: "6px 12px",
                              fontWeight: "var(--fw-bold)",
                              fontSize: "var(--fs-sm)",
                              color: "var(--clr-primary)",
                            }}>
                              {g.clave} &mdash; {g.nombre}
                            </td>
                          </tr>
                          {/* Filas de detalle */}
                          {g.rows.map((r, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid var(--clr-border)", background: i % 2 === 0 ? "#fff" : "var(--clr-table-stripe, #f9fafb)" }}>
                              <td style={scrTd}>{displayDate(r.fecha)}</td>
                              <td style={scrTd}>{(r.seriePago || "").toUpperCase()} {r.folioPago}</td>
                              <td style={scrTd}>{r.tipoDocPago} {r.numDocPago}</td>
                              <td style={scrTd}>{r.tipoDocAPagar} {r.numDocAPagar}</td>
                              <td style={{ ...scrTd, textAlign: "right" }}>{fmt(r.subtotal)}</td>
                              <td style={{ ...scrTd, textAlign: "right" }}>{fmt(r.iva)}</td>
                              <td style={{ ...scrTd, textAlign: "right" }}>{fmt(r.total)}</td>
                            </tr>
                          ))}
                          {/* Total cliente */}
                          <tr style={{ borderTop: "1px solid var(--clr-border)", background: "var(--clr-table-head-bg, #f3f4f6)" }}>
                            <td colSpan={4} style={{ ...scrTd, textAlign: "right", fontWeight: "var(--fw-bold)" }}>TOTAL</td>
                            <td style={{ ...scrTd, textAlign: "right", fontWeight: "var(--fw-bold)" }}>{fmt(totCte.subtotal)}</td>
                            <td style={{ ...scrTd, textAlign: "right", fontWeight: "var(--fw-bold)" }}>{fmt(totCte.iva)}</td>
                            <td style={{ ...scrTd, textAlign: "right", fontWeight: "var(--fw-bold)" }}>{fmt(totCte.total)}</td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totales globales */}
              <div style={{
                display: "flex",
                gap: "var(--sp-6)",
                justifyContent: "flex-end",
                padding: "12px 20px",
                borderTop: "2px solid var(--clr-border)",
                fontWeight: "var(--fw-bold)",
                fontSize: "var(--fs-sm)",
              }}>
                <span>Subtotal: {fmt(totalesGlobal.subtotal)}</span>
                <span>IVA: {fmt(totalesGlobal.iva)}</span>
                <span style={{ fontSize: "var(--fs-md)", color: "var(--clr-primary)" }}>
                  Total: {fmt(totalesGlobal.total)}
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Vista de impresión */}
      <div className="reporte-solo-print">
        <PrintView
          grupos={grupos}
          fechaFin={consultado.fechaFin}
          moneda={consultado.moneda}
          totalesGlobal={totalesGlobal}
        />
      </div>

      <style>{`
        @media screen { .reporte-solo-print { display: none; } }
        @media print  {
          .reporte-no-print { display: none !important; }
          .reporte-solo-print { display: block !important; }
        }
      `}</style>
    </>
  );
}

const scrTh = { padding: "8px 12px", textAlign: "center", fontWeight: "var(--fw-semibold)", fontSize: "var(--fs-sm)" };
const scrTd = { padding: "7px 12px", textAlign: "center" };
